import { KeyValuePipe } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { AdvancedSearchOption, TrimOptionState } from 'remarketing-angular-library';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AdvancedSearchService } from 'src/search/services/advanced-search/advanced-search.service';
import { TranslocoDirective } from '@jsverse/transloco';
import { AccordionModule } from 'ngx-bootstrap/accordion';

@Component({
    selector: 'app-advanced-search-trim',
    templateUrl: './advanced-search-trim.component.html',
    styleUrls: ['./advanced-search-trim.component.scss'],
    providers: [KeyValuePipe],
    imports: [TranslocoDirective, AccordionModule]
})
export class AdvancedSearchTrimComponent implements OnInit, OnChanges {
  @Output() closePop = new EventEmitter();
  @Input() showDropdown: boolean;
  @Input() trimVisible: boolean;
  collapseAll = false;

  trimOptions$: Observable<TrimOptionState> = this.advancedSearchService.trimOptions;
  filteredTrimOptions: any = [];
  formattedTrimOptions: any;
  makeModelTrimOptions: any = []; // {make: "", model: "" trim: ""}
  flattenedTrimOptions: any = [];

  selectedTrimOptions: Array<AdvancedSearchOption> = [];
  selectedTrimOptionsValue: any = []; // {make: "", model: "" trim: ""}

  searchTrim: string;

  selectedTrimCount: { [trim: string]: number } = {};

  private destroyed$ = new Subject();

  constructor(private advancedSearchService: AdvancedSearchService, private keyValuePipe: KeyValuePipe) {}

  ngOnInit() {
    this.trimOptions$.pipe(takeUntil(this.destroyed$)).subscribe(trimOptionState => {
      this.formattedTrimOptions = Object.entries(trimOptionState.formattedOptions).map(([key, value]) => [
        key,
        value[0].vehicleTrims
      ]);
      this.filteredTrimOptions = this.alphabatizeMakeModelTrims(trimOptionState.filteredTrimOptions);
      this.selectedTrimOptions = trimOptionState.selectedTrimOptions;
      this.selectedTrimOptionsValue = trimOptionState.selectedTrimOptionsValues;

      this.assignFlattenedTrimOptions();

      this.calculateTrimOptionState(this.selectedTrimOptions);
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.trimVisible) {
      const change = changes.trimVisible;
      if (change.previousValue === true && change.currentValue === false) {
        this.collapseAll = true;
      }
    }
  }

  alphabatizeMakeModelTrims(data: Array<AdvancedSearchOption>) {
    return data.sort((a, b) =>
      (a.vehicleMake + ' ' + a.vehicleModel).localeCompare(b.vehicleMake + ' ' + b.vehicleModel)
    );
  }

  getAlphabeticalTrims(trimArray: string[]) {
    return trimArray.sort();
  }

  selectTrim(trimOption: AdvancedSearchOption, trim: string) {
    const makeModelTrim = { make: trimOption.vehicleMake, model: trimOption.vehicleModel, trim: trim, isOpen: true };
    let trimFound = false;

    this.selectedTrimOptionsValue.forEach(selectedTrimOptionsValue => {
      // tslint:disable-next-line: max-line-length
      if (
        selectedTrimOptionsValue.model === makeModelTrim.model &&
        selectedTrimOptionsValue.trim === makeModelTrim.trim
      ) {
        // TODO is not properly finding values in array
        const index = this.selectedTrimOptionsValue.findIndex(trimName => {
          return trimName.trim === trim;
        });
        this.removeTrimOption(trimOption, trim, index);
        trimFound = true;
      }
    });

    if (!trimFound) {
      this.addTrimOption(trimOption, trim);
    }
  }

  private addTrimOption(trimOption: AdvancedSearchOption, trim: string) {
    const trimOpt = { ...trimOption, vehicleTrim: trim };
    this.selectedTrimOptions.push(trimOpt);
    // tslint:disable-next-line: max-line-length
    this.selectedTrimOptionsValue.push({ make: trimOption.vehicleMake, model: trimOption.vehicleModel, trim: trim });
  }

  private removeTrimOption(trimOption: AdvancedSearchOption, trim: string, index: number) {
    this.selectedTrimOptionsValue = this.selectedTrimOptionsValue.filter(obj => {
      return !(obj.trim === trim && obj.model === trimOption.vehicleModel);
    });

    this.selectedTrimOptions = this.selectedTrimOptions.filter(obj => {
      return !(obj.vehicleTrim === trim && obj.vehicleModel === trimOption.vehicleModel);
    });

    this.updateTrimState();
  }

  private updateTrimState() {
    const state = this.advancedSearchService._trimOptions.getValue();
    const selectedTrimOptions = this.selectedTrimOptions;
    const selectedTrimOptionsValues = this.selectedTrimOptionsValue;
    const filteredTrimOptions = this.filteredTrimOptions;

    this.advancedSearchService._trimOptions.next({
      ...state,
      selectedTrimOptions,
      selectedTrimOptionsValues,
      filteredTrimOptions
    });
  }

  checked(trimOption: AdvancedSearchOption, trim: string) {
    if (
      this.selectedTrimOptionsValue.filter(obj => {
        return obj.trim === trim && obj.model === trimOption.vehicleModel;
      }).length > 0
    ) {
      return true;
    } else {
      return false;
    }
  }

  onInput(e) {
    this.searchTrim = e.target.value.replace(/[^ -~]+/g, '').toLowerCase();

    if (this.searchTrim) {
      this.trimOptions$.pipe(takeUntil(this.destroyed$)).subscribe(modelOptionState => {
        this.filteredTrimOptions = modelOptionState.filteredTrimOptions;
        const filteredTrimOptions: Array<AdvancedSearchOption> = [];
        let filteredTrimOption: any;
        let vehicleTrimsFiltered = [];
        this.filteredTrimOptions.forEach(makeModel => {
          vehicleTrimsFiltered = makeModel.vehicleTrims.filter(trim => trim.toLowerCase().includes(this.searchTrim));
          filteredTrimOption = { ...makeModel, vehicleTrims: vehicleTrimsFiltered };
          if (filteredTrimOption.vehicleTrims.length !== 0) {
            filteredTrimOptions.push(filteredTrimOption as AdvancedSearchOption);
          }
        });

        this.filteredTrimOptions = filteredTrimOptions;

        this.formattedTrimOptions = this.advancedSearchService.makeModelTrimFormatting(this.filteredTrimOptions);
        this.assignFlattenedTrimOptions();
      });
    } else {
      this.trimOptions$.pipe(takeUntil(this.destroyed$)).subscribe(trimOptionState => {
        this.filteredTrimOptions = trimOptionState.filteredTrimOptions;
        this.formattedTrimOptions = trimOptionState.formattedOptions;
        this.assignFlattenedTrimOptions();
      });
    }
  }

  close() {
    this.closePop.emit();
  }

  calculateTrimOptionState(selectedTrimOptions: AdvancedSearchOption[]) {
    this.selectedTrimCount = selectedTrimOptions.reduce((counts, trim) => {
      if (typeof counts[trim.vehicleTrim] === 'undefined') {
        counts[trim.vehicleTrim] = 0;
      }
      counts[trim.vehicleTrim] += 1;
      return counts;
    }, {});
  }

  assignFlattenedTrimOptions() {
    this.flattenedTrimOptions = this.filteredTrimOptions;
    this.filteredTrimOptions.forEach(filteredTrimOption => {
      filteredTrimOption.vehicleTrims.forEach(vehicleTrim => {
        this.makeModelTrimOptions.push({
          make: filteredTrimOption.vehicleMake,
          model: filteredTrimOption.vehicleModel,
          trim: vehicleTrim,
          isOpen: filteredTrimOption.isOpen
        });
      });
    });
  }

  toggleOpenChange($event) {
    this.collapseAll = false;
    return $event;
  }

  /**
   * Used to prevent closure of the dropdown when anywhere within the advancedSearchTrimComponent is clicked
   */
  onClick(evt) {
    evt.stopPropagation();
    evt.stopImmediatePropagation();
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  getInputId(item: AdvancedSearchOption, trim: string): string {
    return item.vehicleMake + '_' + item.vehicleModel + '_' + trim;
  }
}
